import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

interface CardProps {
  children: ReactNode;
  variant?: 'default' | 'elevated' | 'highlighted' | 'cta';
  className?: string;
  hoverable?: boolean;
}

export function Card({ children, variant = 'default', className, hoverable = false }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-2xl',
        variant === 'default' && [
          'bg-slate-800/40 border border-slate-700/40',
          'shadow-inner-highlight',
        ],
        variant === 'elevated' && [
          'bg-gradient-to-b from-slate-700/50 to-slate-800/60',
          'border border-slate-600/40',
          'shadow-elevated shadow-inner-highlight',
          'card-top-shine',
        ],
        variant === 'highlighted' && [
          'bg-gradient-to-b from-indigo-800/25 to-slate-800/50',
          'border border-indigo-400/30',
          'shadow-card-glow-indigo',
          'ring-1 ring-indigo-400/15',
          'card-top-shine',
        ],
        variant === 'cta' && [
          'bg-gradient-to-br from-indigo-800/30 via-slate-800/50 to-purple-800/30',
          'border border-indigo-400/20',
          'shadow-card-glow-purple',
          'card-top-shine',
        ],
        hoverable && 'transition-all duration-300 hover:border-indigo-400/30 hover:shadow-card-hover hover:-translate-y-0.5',
        className
      )}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps {
  children: ReactNode;
  className?: string;
}

export function CardHeader({ children, className }: CardHeaderProps) {
  return (
    <div
      className={cn(
        'bg-slate-800/80 p-5 border-b border-slate-700/50',
        className
      )}
    >
      {children}
    </div>
  );
}

interface CardContentProps {
  children: ReactNode;
  className?: string;
}

export function CardContent({ children, className }: CardContentProps) {
  return (
    <div
      className={cn(
        'p-6 max-h-[65vh] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700/50 scrollbar-track-transparent',
        className
      )}
    >
      {children}
    </div>
  );
}
